import { useTranslation, buildImageProxyUrl, OrderLineItem } from '@dukanti/shared';
import type { OrderItem } from '@dukanti/shared';
import { env } from '@dukanti/shared/env';

export interface OrderDetailLineItemsProps {
  readonly items: OrderItem[];
}

/**
 * F-022: Line items section for the order detail page.
 *
 * Renders each order item using the shared <OrderLineItem> component, wrapped
 * in an admin-specific <a> link to `/products/<productId>` (opens in a new
 * tab). The shared component has no link slot, so the link wraps the full row.
 *
 * Images are resolved via `buildImageProxyUrl`. Null paths render a
 * placeholder (handled by the shared component).
 */
export function OrderDetailLineItems({ items }: OrderDetailLineItemsProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section
      data-testid="order-detail-line-items"
      className="border-border bg-card shadow-admin-card rounded-lg border p-6"
    >
      <h2 className="text-muted-foreground mb-4 text-xs font-semibold uppercase tracking-wide">
        {t('admin.orders.detail.lineItems')}
      </h2>

      <div className="flex flex-col">
        {items.map((item, index) => {
          const imageUrl = item.product_image_path_snapshot
            ? buildImageProxyUrl(
                env.VITE_SUPABASE_URL,
                'product-images',
                item.product_image_path_snapshot,
              )
            : null;
          return (
            <div key={item.id}>
              {index > 0 && <hr className="border-border" />}
              {/* Admin-specific: wrap the row in a product link (shared component has no link slot) */}
              <a
                href={`/products/${item.product_id}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('admin.orders.detail.viewProduct')}
                data-testid="order-line-item-product-link"
                className="block"
              >
                <OrderLineItem
                  name={item.product_name_snapshot}
                  variantSummary={item.variant_option_summary_snapshot}
                  imagePath={item.product_image_path_snapshot}
                  imageUrl={imageUrl}
                  quantity={item.quantity}
                  unitPriceCents={item.unit_price_snapshot_cents}
                  lineTotalCents={item.line_total_cents}
                />
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}
